import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Eye, EyeOff, Mail, RotateCcw, Link2, Search, Users, UserCog, Shield,
  LinkIcon, Unlink, Lock, CheckCircle2, AlertCircle, User
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManageAccounts = () => {
  const [accounts, setAccounts] = useState({ athletes: [], staff: [], parents: [], admins: [] });
  const [allAthletes, setAllAthletes] = useState([]);
  const [athletesForLinking, setAthletesForLinking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [revealing, setRevealing] = useState({});

  // Dialogs
  const [emailDialog, setEmailDialog] = useState({ open: false, user: null });
  const [newEmail, setNewEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const [resetDialog, setResetDialog] = useState({ open: false, user: null });
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  // Parent multi-select link dialog
  const [linkDialog, setLinkDialog] = useState({ open: false, user: null });
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [linkSearch, setLinkSearch] = useState('');
  const [savingLinks, setSavingLinks] = useState(false);

  // Player single-select link dialog
  const [playerLinkDialog, setPlayerLinkDialog] = useState({ open: false, user: null });
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [playerLinkSearch, setPlayerLinkSearch] = useState('');
  const [savingPlayerLink, setSavingPlayerLink] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [acctRes, athRes, linkRes] = await Promise.all([
        axios.get(`${API}/admin/manage-accounts`),
        axios.get(`${API}/athletes`),
        axios.get(`${API}/admin/athletes-for-linking`)
      ]);
      setAccounts(acctRes.data);
      setAllAthletes(athRes.data || []);
      setAthletesForLinking(linkRes.data || []);
    } catch {
      toast.error('Failed to fetch account data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReveal = async (userId) => {
    if (revealedPasswords[userId]) {
      setRevealedPasswords(prev => { const c = { ...prev }; delete c[userId]; return c; });
      return;
    }
    setRevealing(prev => ({ ...prev, [userId]: true }));
    try {
      const res = await axios.post(`${API}/admin/manage-accounts/${userId}/reveal`);
      setRevealedPasswords(prev => ({ ...prev, [userId]: res.data.password }));
      setTimeout(() => {
        setRevealedPasswords(prev => { const c = { ...prev }; delete c[userId]; return c; });
      }, 15000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reveal password');
    } finally {
      setRevealing(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Email edit
  const openEmailDialog = (user) => {
    setEmailDialog({ open: true, user });
    setNewEmail(user.email || '');
  };

  const saveEmail = async () => {
    if (!emailDialog.user || !newEmail.trim()) return;
    setSavingEmail(true);
    try {
      await axios.put(`${API}/admin/manage-accounts/${emailDialog.user.id}/email`, { email: newEmail.trim() });
      toast.success(`Email updated for ${emailDialog.user.full_name}`);
      setEmailDialog({ open: false, user: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  // Password reset
  const openResetDialog = (user) => {
    setResetDialog({ open: true, user });
    setResetPassword('');
  };

  const handleReset = async () => {
    if (!resetDialog.user || !resetPassword) return;
    setResetting(true);
    try {
      await axios.post(`${API}/admin/manage-accounts/${resetDialog.user.id}/reset`, { new_password: resetPassword });
      toast.success(`Password reset for ${resetDialog.user.full_name}`);
      setResetDialog({ open: false, user: null });
      setRevealedPasswords(prev => { const c = { ...prev }; delete c[resetDialog.user.id]; return c; });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  // Parent-athlete linking (multi-select)
  const openLinkDialog = (user) => {
    setLinkDialog({ open: true, user });
    setSelectedAthletes(user.linked_athletes?.map(a => a.id) || []);
    setLinkSearch('');
  };

  const toggleAthleteLink = (athleteId) => {
    setSelectedAthletes(prev =>
      prev.includes(athleteId) ? prev.filter(id => id !== athleteId) : [...prev, athleteId]
    );
  };

  const saveLinks = async () => {
    if (!linkDialog.user) return;
    setSavingLinks(true);
    try {
      await axios.put(`${API}/admin/manage-accounts/${linkDialog.user.id}/link-athletes`, { athlete_ids: selectedAthletes });
      toast.success(`Athlete links updated for ${linkDialog.user.full_name}`);
      setLinkDialog({ open: false, user: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update links');
    } finally {
      setSavingLinks(false);
    }
  };

  // Player-athlete linking (single-select)
  const openPlayerLinkDialog = (user) => {
    setPlayerLinkDialog({ open: true, user });
    setSelectedAthlete(user.linked_athletes?.[0]?.id || null);
    setPlayerLinkSearch('');
  };

  const savePlayerLink = async () => {
    if (!playerLinkDialog.user) return;
    setSavingPlayerLink(true);
    try {
      await axios.put(
        `${API}/admin/manage-accounts/${playerLinkDialog.user.id}/link-athlete`,
        { athlete_id: selectedAthlete }
      );
      toast.success(
        selectedAthlete
          ? `Athlete linked to ${playerLinkDialog.user.full_name}`
          : `Athlete unlinked from ${playerLinkDialog.user.full_name}`
      );
      setPlayerLinkDialog({ open: false, user: null });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update athlete link');
    } finally {
      setSavingPlayerLink(false);
    }
  };

  const filterUsers = (list) => {
    if (!searchTerm.trim()) return list;
    const q = searchTerm.toLowerCase();
    return list.filter(u =>
      u.full_name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.linked_athletes?.some(a => a.name?.toLowerCase().includes(q))
    );
  };

  const filteredLinkAthletes = allAthletes.filter(a =>
    !linkSearch.trim() || a.full_name?.toLowerCase().includes(linkSearch.toLowerCase())
  );

  const filteredPlayerLinkAthletes = athletesForLinking.filter(a =>
    !playerLinkSearch.trim() || a.full_name?.toLowerCase().includes(playerLinkSearch.toLowerCase())
  );

  // Render table for each tab
  const renderTable = (users, tabType) => {
    const filtered = filterUsers(users);
    const showLinkColumn = tabType === 'athletes' || tabType === 'parents';
    const isAthleteTab = tabType === 'athletes';
    const isParentTab = tabType === 'parents';

    if (filtered.length === 0) {
      return <p className="text-center text-gray-500 dark:text-gray-400 py-8" data-testid="no-accounts-msg">No accounts found</p>;
    }

    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 dark:bg-gray-800/50">
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="font-semibold">Role</TableHead>
              {showLinkColumn && <TableHead className="font-semibold">Linked Athlete(s)</TableHead>}
              <TableHead className="font-semibold">Email</TableHead>
              <TableHead className="font-semibold">Password</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((user) => {
              const isLinked = user.is_linked || (user.linked_athletes && user.linked_athletes.length > 0);
              const emailImmutable = (user.role === 'Player' || user.role === 'Parent') && isLinked;

              return (
                <TableRow key={user.id} data-testid={`account-row-${user.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-xs font-semibold text-emerald-700 dark:text-emerald-300 flex-shrink-0">
                        {user.full_name?.charAt(0) || '?'}
                      </div>
                      <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">{user.full_name}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] px-1.5 py-0 ${
                      user.role === 'Admin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' :
                      user.role === 'Staff' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' :
                      user.role === 'Parent' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' :
                      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    }`}>{user.role}</Badge>
                  </TableCell>
                  {showLinkColumn && (
                    <TableCell>
                      {user.linked_athletes?.length > 0 ? (
                        <div className="flex flex-wrap gap-1 items-center">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          {user.linked_athletes.map(a => (
                            <Badge key={a.id} variant="outline" className="text-xs bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">{a.name}</Badge>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Not linked</span>
                        </div>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {emailImmutable && <Lock className="w-3 h-3 text-gray-400" />}
                      <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">{user.email || '\u2014'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-[120px]">
                      {revealedPasswords[user.id] ? (
                        <code className="text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 px-2 py-1 rounded font-mono" data-testid={`revealed-pw-${user.id}`}>
                          {revealedPasswords[user.id]}
                        </code>
                      ) : (
                        <span className="text-gray-400 tracking-widest">{'\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      {/* Link Athlete (Players) */}
                      {isAthleteTab && (
                        <Button
                          size="sm" variant="outline"
                          className={`h-7 px-2 text-xs ${isLinked ? 'border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-400' : 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400'}`}
                          onClick={() => openPlayerLinkDialog(user)}
                          data-testid={`link-athlete-${user.id}`}
                        >
                          {isLinked ? <><LinkIcon className="w-3.5 h-3.5 mr-1" /> Linked</> : <><Unlink className="w-3.5 h-3.5 mr-1" /> Link</>}
                        </Button>
                      )}
                      {/* Assign Athletes (Parents) */}
                      {isParentTab && (
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openLinkDialog(user)} data-testid={`link-athletes-${user.id}`}>
                          <Link2 className="w-3.5 h-3.5 mr-1" /> Assign
                        </Button>
                      )}
                      {/* Email Edit */}
                      <Button
                        size="sm" variant="outline"
                        className={`h-7 px-2 text-xs ${emailImmutable ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => !emailImmutable && openEmailDialog(user)}
                        disabled={emailImmutable}
                        data-testid={`edit-email-${user.id}`}
                        title={emailImmutable ? 'Email is locked after linking. Unlink to edit.' : 'Edit email'}
                      >
                        {emailImmutable ? <Lock className="w-3.5 h-3.5 mr-1" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
                        Email
                      </Button>
                      {/* Show/Hide Password */}
                      <Button
                        size="sm" variant="outline" className="h-7 px-2 text-xs"
                        onClick={() => handleReveal(user.id)}
                        disabled={revealing[user.id]}
                        data-testid={`reveal-pw-${user.id}`}
                      >
                        {revealing[user.id] ? '...' : revealedPasswords[user.id] ? (
                          <><EyeOff className="w-3.5 h-3.5 mr-1" /> Hide</>
                        ) : (
                          <><Eye className="w-3.5 h-3.5 mr-1" /> Show</>
                        )}
                      </Button>
                      {/* Reset Password */}
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openResetDialog(user)} data-testid={`reset-pw-${user.id}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="manage-accounts-page">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Manage Accounts
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">Link athletes, manage emails, passwords, and parent-athlete access</p>
        </div>

        {/* Search */}
        <div className="mb-4 max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, or athlete..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="account-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <Tabs defaultValue="athletes" data-testid="accounts-tabs">
            <TabsList className="mb-4">
              <TabsTrigger value="athletes" data-testid="tab-athletes" className="gap-1.5">
                <Users className="w-4 h-4" />
                Athletes
                <Badge variant="secondary" className="ml-1 text-[10px] h-5">{accounts.athletes.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="staff" data-testid="tab-staff" className="gap-1.5">
                <UserCog className="w-4 h-4" />
                Staff
                <Badge variant="secondary" className="ml-1 text-[10px] h-5">{accounts.staff.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="parents" data-testid="tab-parents" className="gap-1.5">
                <Shield className="w-4 h-4" />
                Parents
                <Badge variant="secondary" className="ml-1 text-[10px] h-5">{accounts.parents.length}</Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="athletes">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Athlete Accounts</CardTitle>
                  <CardDescription>Link player accounts to athlete profiles for secure, isolated access</CardDescription>
                </CardHeader>
                <CardContent>{renderTable(accounts.athletes, 'athletes')}</CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="staff">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Staff Accounts</CardTitle>
                  <CardDescription>Manage emails and passwords for coaches, physio, and other staff</CardDescription>
                </CardHeader>
                <CardContent>{renderTable(accounts.staff, 'staff')}</CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="parents">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Parent Accounts</CardTitle>
                  <CardDescription>Assign athletes to parent accounts for controlled data access</CardDescription>
                </CardHeader>
                <CardContent>{renderTable(accounts.parents, 'parents')}</CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Edit Email Dialog */}
        <Dialog open={emailDialog.open} onOpenChange={(open) => !open && setEmailDialog({ open: false, user: null })}>
          <DialogContent data-testid="edit-email-dialog">
            <DialogHeader>
              <DialogTitle>Edit Email &mdash; {emailDialog.user?.full_name}</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <p className="text-sm text-gray-500">
                Current email: <strong>{emailDialog.user?.email || 'None'}</strong>
              </p>
              <div>
                <Label>New Email</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email"
                  data-testid="new-email-input"
                />
              </div>
              <p className="text-xs text-gray-400">This change will be logged for audit purposes.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailDialog({ open: false, user: null })}>Cancel</Button>
              <Button onClick={saveEmail} disabled={!newEmail.trim() || savingEmail} data-testid="save-email-btn">
                {savingEmail ? 'Saving...' : 'Save Email'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetDialog.open} onOpenChange={(open) => !open && setResetDialog({ open: false, user: null })}>
          <DialogContent data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle>Reset Password &mdash; {resetDialog.user?.full_name}</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <p className="text-sm text-gray-500">
                Set a new password for <strong>{resetDialog.user?.email}</strong>. This will be logged.
              </p>
              <div>
                <Label>New Password</Label>
                <Input
                  type="text"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="Enter new password (min 6 chars)"
                  data-testid="reset-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetDialog({ open: false, user: null })}>Cancel</Button>
              <Button onClick={handleReset} disabled={!resetPassword || resetPassword.length < 6 || resetting} data-testid="reset-confirm-btn">
                {resetting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Athletes Dialog (Parents — Multi-select) */}
        <Dialog open={linkDialog.open} onOpenChange={(open) => !open && setLinkDialog({ open: false, user: null })}>
          <DialogContent className="max-w-lg" data-testid="link-athletes-dialog">
            <DialogHeader>
              <DialogTitle>Assign Athletes &mdash; {linkDialog.user?.full_name}</DialogTitle>
            </DialogHeader>
            <div className="py-2 space-y-3">
              <p className="text-sm text-gray-500">
                Select which athletes this parent account can access.
              </p>
              <Input
                placeholder="Search athletes..."
                value={linkSearch}
                onChange={(e) => setLinkSearch(e.target.value)}
                data-testid="link-search-input"
              />
              <div className="max-h-[280px] overflow-y-auto space-y-1 border rounded-lg p-2 dark:border-gray-700">
                {filteredLinkAthletes.map(a => (
                  <label
                    key={a.id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      selectedAthletes.includes(a.id) ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''
                    }`}
                    data-testid={`link-option-${a.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAthletes.includes(a.id)}
                      onChange={() => toggleAthleteLink(a.id)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-800 dark:text-gray-200">{a.full_name}</span>
                    {a.position && <Badge variant="outline" className="text-[10px] ml-auto">{a.position}</Badge>}
                  </label>
                ))}
                {filteredLinkAthletes.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">No athletes found</p>
                )}
              </div>
              <p className="text-xs text-gray-500">{selectedAthletes.length} athlete(s) selected</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkDialog({ open: false, user: null })}>Cancel</Button>
              <Button onClick={saveLinks} disabled={savingLinks} data-testid="save-links-btn">
                {savingLinks ? 'Saving...' : 'Save Links'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Athlete Dialog (Players — Single-select) */}
        <Dialog open={playerLinkDialog.open} onOpenChange={(open) => !open && setPlayerLinkDialog({ open: false, user: null })}>
          <DialogContent className="max-w-lg" data-testid="player-link-dialog">
            <DialogHeader>
              <DialogTitle>Link Athlete &mdash; {playerLinkDialog.user?.full_name}</DialogTitle>
            </DialogHeader>
            <div className="py-2 space-y-3">
              <p className="text-sm text-gray-500">
                Select the athlete profile to link with this player account. Once linked, the player will only see their own data.
              </p>
              <Input
                placeholder="Search athletes..."
                value={playerLinkSearch}
                onChange={(e) => setPlayerLinkSearch(e.target.value)}
                data-testid="player-link-search-input"
              />
              <div className="max-h-[300px] overflow-y-auto space-y-1 border rounded-lg p-2 dark:border-gray-700">
                {/* Unlink option */}
                <label
                  className={`flex items-center gap-2 px-2 py-2 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                    selectedAthlete === null ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : ''
                  }`}
                  data-testid="player-link-unlink"
                >
                  <input
                    type="radio"
                    name="playerAthleteLink"
                    checked={selectedAthlete === null}
                    onChange={() => setSelectedAthlete(null)}
                    className="text-red-600"
                  />
                  <Unlink className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-600 dark:text-red-400 font-medium">No link (unlinked)</span>
                </label>

                {filteredPlayerLinkAthletes.map(a => {
                  const isOtherLinked = a.linked_player && a.linked_player !== playerLinkDialog.user?.id;
                  const isSelfLinked = selectedAthlete === a.id;

                  return (
                    <label
                      key={a.id}
                      className={`flex items-center gap-2 px-2 py-2 rounded transition-colors ${
                        isOtherLinked
                          ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800'
                          : isSelfLinked
                            ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 cursor-pointer'
                            : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      data-testid={`player-link-option-${a.id}`}
                    >
                      <input
                        type="radio"
                        name="playerAthleteLink"
                        checked={isSelfLinked}
                        onChange={() => !isOtherLinked && setSelectedAthlete(a.id)}
                        disabled={isOtherLinked}
                        className="text-emerald-600"
                      />
                      <User className="w-4 h-4 text-gray-500" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-800 dark:text-gray-200">{a.full_name}</span>
                        {a.position && <span className="text-xs text-gray-400 ml-2">{a.position}</span>}
                      </div>
                      {isOtherLinked && (
                        <Badge variant="outline" className="text-[10px] text-red-500 border-red-300 dark:border-red-700 flex-shrink-0">
                          Linked to {a.linked_player_name || 'another'}
                        </Badge>
                      )}
                      {a.linked_player && !isOtherLinked && (
                        <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300 dark:border-emerald-700 flex-shrink-0">
                          Currently linked
                        </Badge>
                      )}
                    </label>
                  );
                })}
                {filteredPlayerLinkAthletes.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">No athletes found</p>
                )}
              </div>
              {selectedAthlete && (
                <div className="flex items-center gap-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm text-emerald-700 dark:text-emerald-300">
                    Will link to: <strong>{athletesForLinking.find(a => a.id === selectedAthlete)?.full_name}</strong>
                  </span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPlayerLinkDialog({ open: false, user: null })}>Cancel</Button>
              <Button
                onClick={savePlayerLink}
                disabled={savingPlayerLink}
                className={selectedAthlete === null ? 'bg-red-600 hover:bg-red-700' : ''}
                data-testid="save-player-link-btn"
              >
                {savingPlayerLink ? 'Saving...' : selectedAthlete === null ? 'Unlink' : 'Link Athlete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ManageAccounts;
